import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Paper,
  Button,
  Grid,
  Select,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  CircularProgress,
  TextField,
  Autocomplete,
  Radio,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Slider from "@mui/material/Slider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { useAccessToken } from "../../auth/useAccessToken";
import {
  fetchAlertPreferenceExceptionType,
  saveAlertSubscription,
  fetchRegions,
  fetchPlants,
  fetchMaterialTypes,
  fetchMrpControllers,
  fetchMaterials,
  deleteAlertSubscription,
} from "../../services/Alerts/AlertPreferencesService";
import { updateAlertSubscription } from "../../services/Alerts/AlertPreferencesService";
import { fetchAlertName } from "../../services/Alerts/AlertsService";

const categories = [
  "Inventory",
  "Purchase Order",
  "Inbound Shipment",
  "Sales Order",
  "Outbound Shipment",

];

// Map UI category labels to backend enum codes
const CATEGORY_TO_CODE = {
  Inventory: "INVENTORY",
  "Purchase Order": "STANDARD PURCHASE ORDER",
  "Inbound Shipment": "INBOUND SHIPMENTS",
  "Sales Order": "SALES ORDER",
  "Outbound Shipment": "OUTBOUND SHIPMENTS",
  "Stock Transfer": "STOCK TRANSFER ORDER",
};

// Reverse map backend enum codes to UI labels
const CODE_TO_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_TO_CODE).map(([k, v]) => [v, k])
);

const initialFilterState = {
  region: [],
  siteAttribute: [],
  itemAttribute: [],
  mrpController: [],
  materialType: [],
  exception: [],
  inApp: false,
  email: true,
  timeMode: "weeks",
  weeksRange: [0, 52],
  dateRange: { start: null, end: null },
};

const parseAlertDescription = (description) => {
  if (!description) return initialFilterState;

  const lines = description.split("\n").filter((l) => l.trim());
  const parsed = { ...initialFilterState };

  lines.forEach((line) => {
    if (line.includes("- Region:")) {
      const regions = line
        .replace("- Region:", "")
        .trim()
        .split(",")
        .map((r) => r.trim());
      parsed.region = regions;
    } else if (line.includes("- Plant:")) {
      const plants = line
        .replace("- Plant:", "")
        .trim()
        .split(",")
        .map((p) => p.trim());
      parsed.siteAttribute = plants;
    } else if (line.includes("- Material Type:")) {
      const types = line
        .replace("- Material Type:", "")
        .trim()
        .split(",")
        .map((t) => t.trim());
      parsed.materialType = types;
    } else if (line.includes("- Material:")) {
      const materials = line
        .replace("- Material:", "")
        .trim()
        .split(",")
        .map((m) => m.trim());
      parsed.itemAttribute = materials;
    } else if (line.includes("- MRP Controller:")) {
      const mrps = line
        .replace("- MRP Controller:", "")
        .trim()
        .split(",")
        .map((m) => m.trim());
      parsed.mrpController = mrps;
    } else if (line.includes("- Exception:")) {
      const exceptions = line
        .replace("- Exception:", "")
        .trim()
        .split(",")
        .map((e) => e.trim());
      parsed.exception = exceptions;
    } else if (line.includes("- Time Horizon:")) {
      const timeStr = line.replace("- Time Horizon:", "").trim();
      const match = timeStr.match(/(\d+)\s+to\s+(\d+)\s+weeks/);
      if (match) {
        parsed.weeksRange = [parseInt(match[1]), parseInt(match[2])];
        parsed.timeMode = "weeks";
      }
    }

    // Parse Email/App channel
    if (line.includes("Alert me on")) {
      if (line.includes("Email")) {
        parsed.email = true;
      }
      if (line.includes("App")) {
        parsed.inApp = true;
      }
    }
  });

  return parsed;
};

const detectCategoryFromDescription = (description) => {
  if (!description) return "Inventory";

  // Extract category from description: "Alert me on Email for [CATEGORY] when:"
  const categoryMatch = description.match(
    /Alert me on \w+ for ([\w\s]+) when:/i
  );

  if (categoryMatch) {
    const detectedCategory = categoryMatch[1].trim();

    // Validate against known categories
    if (detectedCategory === "Inventory") return "Inventory";
    if (detectedCategory === "Purchase Order") return "Purchase Order";
    if (detectedCategory === "Inbound Shipment") return "Inbound Shipment";
    if (detectedCategory === "Sales Order") return "Sales Order";
    if (detectedCategory === "Outbound Shipment") return "Outbound Shipment";
    if (detectedCategory === "Stock Transfer") return "Stock Transfer";
  }

  return "Inventory";
};

const SELECT_ALL = "__SELECT_ALL__";

const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
};


const MultiSelectCheckbox = ({
  label,
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = "Select one or more",
  width = 200,
  isLoading = false,
  enableSearch = true,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", width }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2">Loading {label}...</Typography>
      </Box>
    );
  }

  const isAllSelected =
    options.length > 0 && value.length === options.length;

  const handleChange = (event, newValue) => {
    if (newValue.includes(SELECT_ALL)) {
      onChange({
        target: {
          value: isAllSelected ? [] : options,
        },
      });
    } else {
      onChange({
        target: {
          value: newValue,
        },
      });
    }
  };

  const computedOptions = [SELECT_ALL, ...options];

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={computedOptions}
      value={value}
      disabled={disabled}
      onChange={handleChange}
      getOptionLabel={(opt) =>
        opt === SELECT_ALL ? "Select All" : opt
      }
      isOptionEqualToValue={(option, value) => option === value}
      filterSelectedOptions={false}
      sx={{ width }}
      ListboxProps={{ style: { maxHeight: 250 } }}
      noOptionsText="No matches found"
      renderOption={(props, option, { selected }) => {
        if (option === SELECT_ALL) {
          return (
            <li {...props}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={
                  value.length > 0 && value.length < options.length
                }
                sx={{ mr: 1 }}
              />
              Select All
            </li>
          );
        }

        return (
          <li {...props}>
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            {option}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
          inputProps={{
            ...params.inputProps,
            readOnly: !enableSearch,
          }}
        />
      )}
    />
  );
};

const AlertPreference = ({ onBack }) => {
  const [selectedCategory, setSelectedCategory] = useState("Inventory");
  const [isGlobalAlertsEnabled, setIsGlobalAlertsEnabled] = useState(true);
  const [categoryStatus, setCategoryStatus] = useState(
    categories.reduce((acc, category) => {
      acc[category] = true;
      return acc;
    }, {})
  );
  const [alertName, setAlertName] = useState("");
  const [currentFilters, setCurrentFilters] = useState(initialFilterState);
  const [allSavedConditions, setAllSavedConditions] = useState([]);
  const [savedConditions, setSavedConditions] = useState([]);
  const [alertNameOptions, setAlertNameOptions] = useState([]);
  const [editingConditionId, setEditingConditionId] = useState(null);
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [apiRegions, setApiRegions] = useState([]);
  const [apiPlants, setApiPlants] = useState([]);
  const [apiMaterialTypes, setApiMaterialTypes] = useState([]);
  const [apiMrpControllers, setApiMrpControllers] = useState([]);
  const [apiMaterials, setApiMaterials] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [isInAppDisabled, setIsInAppDisabled] = useState(true);

  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingPlants, setLoadingPlants] = useState(false);
  const [loadingMaterialTypes, setLoadingMaterialTypes] = useState(false);
  const [loadingMrpControllers, setLoadingMrpControllers] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [loadingExceptions, setLoadingExceptions] = useState(false);
  const [loadingSavedConditions, setLoadingSavedConditions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const { email: reduxEmail } = useSelector((state) => state.user || {});
  const accessToken = useAccessToken();
  const [decodedToken, setDecodedToken] = useState(null);
  const [editingCondition, setEditingCondition] = useState(null);
  const [isEditHydrated, setIsEditHydrated] = useState(false);
  const [materialSearchText, setMaterialSearchText] = useState("");
  const [materialPage, setMaterialPage] = useState(0);
  const [hasMoreMaterials, setHasMoreMaterials] = useState(true);
  const MATERIAL_PAGE_SIZE = 50;




  useEffect(() => {

    if (!accessToken) {
      setDecodedToken(null);
      return;
    }
    try {
      const parsed = jwtDecode(accessToken);
      setDecodedToken(parsed);
    } catch (err) {
      setDecodedToken(null);
    }
  }, [accessToken]);

  useEffect(() => {


    setApiMaterials([]);
    setMaterialPage(0);
    setHasMoreMaterials(true);
  }, [
    currentFilters.siteAttribute,
    currentFilters.materialType,
    selectedCategory
  ]);


  const userEmail =
    decodedToken?.unique_name || decodedToken?.email || reduxEmail || "";

  // Fetch ALL saved alert conditions from API on component mount
  useEffect(() => {
    const fetchAllConditions = async () => {
      try {
        setLoadingSavedConditions(true);
        const code = formatCategoryForPayload(selectedCategory) || "inventory";
        const response = await fetchAlertName(code);

        if (response?.alertNames && Array.isArray(response.alertNames)) {
          setAlertNameOptions(response.alertNames);
        }

        // Extract alert details and parse them - Check for alertDescription instead of alertDetails
        const alertDetails = response?.alertDetails || response?.alertDescription;

        if (alertDetails && Array.isArray(alertDetails)) {
          const conditions = alertDetails.map((detail, idx) => {
            // Prefer explicit backend category code if present, map to UI label
            const backendCode =
              detail.alertCategory ||
              detail.category ||
              detail.alertCategoryCode ||
              detail.categoryCode ||
              null;

            let categoryLabel = null;
            if (backendCode) {
              categoryLabel = selectedCategory || null;
            }

            // Fallback: try to detect from description if no mapped code
            if (!categoryLabel) {
              categoryLabel = detectCategoryFromDescription(detail.description);
            }

            const parsedFilters = parseAlertDescription(detail.description);

            const condition = {
              id: detail.alertCriteriaId || idx,
              alertName: detail.alertName,
              category: categoryLabel,
              summary: detail.description,
              alertStatus: detail.alertStatus !== false,
              ...parsedFilters,
            };

            return condition;
          });

          setAllSavedConditions(conditions);
        } else {
          console.warn(
            "No alertDetails or alertDescription in response or not an array"
          );
          setAllSavedConditions([]);
        }

        setLoadingSavedConditions(false);
      } catch (error) {
        console.error("Failed to fetch saved alert conditions:", error);
        setAllSavedConditions([]);
        setAlertNameOptions([]);
        setLoadingSavedConditions(false);
      }
    };

    fetchAllConditions();
  }, [selectedCategory]);

  // Saved conditions are kept in `allSavedConditions`; derive visible
  // `savedConditions` from the currently selected category.
  useEffect(() => {
    const filtered = allSavedConditions.filter((cond) => {
      console.log("Filtering condition:", formatCategoryForPayload(cond.category), formatCategoryForPayload(selectedCategory));
      return formatCategoryForPayload(cond.category) == formatCategoryForPayload(selectedCategory);
    });

    setSavedConditions(filtered);
  }, [allSavedConditions, selectedCategory]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoadingRegions(true);
      const regionsData = userEmail
        ? await fetchRegions(userEmail)
        : await fetchRegions("");
      let regionsList = [];
      if (Array.isArray(regionsData)) {
        regionsList = regionsData;
      } else if (Array.isArray(regionsData?.content?.regions)) {
        regionsList = regionsData.content.regions;
      } else if (Array.isArray(regionsData?.data?.content?.regions)) {
        regionsList = regionsData.data.content.regions;
      } else {
        regionsList = [];
      }
      setApiRegions(Array.isArray(regionsList) ? regionsList : []);
      setLoadingRegions(false);
    };

    fetchAllData();
  }, [userEmail]);

  useEffect(() => {
    const fetchPlantsData = async () => {
      if (currentFilters.region.length === 0) {
        setApiPlants([]);
        return;
      }

      setLoadingPlants(true);
      const plantsData = await fetchPlants(userEmail, currentFilters.region);
      setApiPlants(Array.isArray(plantsData) ? plantsData : []);
      setLoadingPlants(false);
    };

    fetchPlantsData();
  }, [currentFilters.region, userEmail]);

  useEffect(() => {
    const fetchMaterialTypesData = async () => {
      if (currentFilters.siteAttribute.length === 0) {
        setApiMaterialTypes([]);
        return;
      }

      setLoadingMaterialTypes(true);
      const materialTypesData = await fetchMaterialTypes(
        currentFilters.siteAttribute
      );
      setApiMaterialTypes(
        Array.isArray(materialTypesData) ? materialTypesData : []
      );
      setLoadingMaterialTypes(false);
    };

    fetchMaterialTypesData();
  }, [currentFilters.siteAttribute]);

  useEffect(() => {
    const fetchMrpControllersData = async () => {
      if (
        currentFilters.siteAttribute.length === 0 ||
        currentFilters.materialType.length === 0
      ) {
        setApiMrpControllers([]);
        return;
      }

      setLoadingMrpControllers(true);
      const mrpControllersData = await fetchMrpControllers(
        currentFilters.siteAttribute,
        currentFilters.materialType
      );
      setApiMrpControllers(
        Array.isArray(mrpControllersData) ? mrpControllersData : []
      );
      setLoadingMrpControllers(false);
    };

    fetchMrpControllersData();
  }, [currentFilters.siteAttribute, currentFilters.materialType]);

  const debouncedMaterialSearch = useDebounce(materialSearchText, 500);




  useEffect(() => {

    const shouldFetch =
      currentFilters.siteAttribute.length > 0 &&
      currentFilters.materialType.length > 0 &&
      (
        !isInventory || currentFilters.mrpController.length > 0
      ) &&
      (
        debouncedMaterialSearch === "" ||
        debouncedMaterialSearch.length >= 3
      );


    if (!shouldFetch || !hasMoreMaterials) return;

    const mrpPayload =
      isInventory && currentFilters.mrpController.length > 0
        ? currentFilters.mrpController
        : [];

    const fetchLazyMaterials = async () => {
      try {

        setLoadingMaterials(true);

        const response = await fetchMaterials(
          currentFilters.siteAttribute,
          currentFilters.materialType,
          mrpPayload,
          formatCategoryForPayload(selectedCategory),
          {
            page: materialPage,
            size: MATERIAL_PAGE_SIZE,
            searchText:
              debouncedMaterialSearch.length >= 3
                ? debouncedMaterialSearch
                : "",
          }
        );


        const newMaterials = Array.isArray(response?.content)
          ? response.content
          : Array.isArray(response)
            ? response
            : [];

        setApiMaterials((prev) =>
          materialPage === 0 ? newMaterials : [...prev, ...newMaterials]
        );

        if (newMaterials.length < MATERIAL_PAGE_SIZE) {
          setHasMoreMaterials(false);
        }
      } catch (err) {
        console.log("Material fetch failed");
        console.error("Material fetch failed", err);
      } finally {
        setLoadingMaterials(false);
      }
    };

    fetchLazyMaterials();
  }, [
    debouncedMaterialSearch,
    materialPage,
    currentFilters.siteAttribute,
    currentFilters.materialType,
    currentFilters.mrpController,
    selectedCategory,
  ]);


  // Load dependent data when editing mode is activated
  useEffect(() => {
    const fetchDependentDataForEdit = async () => {
      if (editingConditionId === null) return;
        
      // Fetch plants based on selected regions
      if (currentFilters.region.length > 0) {
        setLoadingPlants(true);
        const plantsData = await fetchPlants(userEmail, currentFilters.region);
        setApiPlants(Array.isArray(plantsData) ? plantsData : []);
        setLoadingPlants(false);
      }

      // Fetch material types based on selected plants
      if (currentFilters.siteAttribute.length > 0) {
        setLoadingMaterialTypes(true);
        const materialTypesData = await fetchMaterialTypes(
          currentFilters.siteAttribute
        );
        setApiMaterialTypes(
          Array.isArray(materialTypesData) ? materialTypesData : []
        );
        setLoadingMaterialTypes(false);
      }


      if (
        currentFilters.siteAttribute.length > 0 &&
        currentFilters.materialType.length > 0
      ) {
        setLoadingMrpControllers(true);
        const mrpControllersData = await fetchMrpControllers(
          currentFilters.siteAttribute,
          currentFilters.materialType
        );
        setApiMrpControllers(
          Array.isArray(mrpControllersData) ? mrpControllersData : []
        );
        setLoadingMrpControllers(false);
      }

      // Fetch materials
      if (
        currentFilters.siteAttribute.length > 0 &&
        currentFilters.materialType.length > 0
      ) {
        setLoadingMaterials(true);
        const mrpPayload =
          isInventory && currentFilters.mrpController.length > 0
            ? currentFilters.mrpController
            : [];
        const materialsData = await fetchMaterials(
          currentFilters.siteAttribute,
          currentFilters.materialType,
          mrpPayload,
          formatCategoryForPayload(selectedCategory),
          {
            page: 0,
            size: MATERIAL_PAGE_SIZE,
            searchText: "",
          }
        );
        const newMaterials = Array.isArray(materialsData?.content)
          ? materialsData.content
          : Array.isArray(materialsData)
            ? materialsData
            : [];
        setApiMaterials(newMaterials);
        setLoadingMaterials(false);
      }
    };

    fetchDependentDataForEdit();
  }, [editingConditionId, currentFilters, userEmail]);

  useEffect(() => {
    const fetchExceptions = async () => {
      setLoadingExceptions(true);
      const code = formatCategoryForPayload(selectedCategory) || "inventory";

      const data = await fetchAlertPreferenceExceptionType(code);
      const exceptionsList = Array.isArray(data?.content?.exceptions)
        ? data.content.exceptions
        : [];
      setExceptions(exceptionsList);
      setLoadingExceptions(false);
    };

    fetchExceptions();
  }, [selectedCategory]);

  const availableExceptions = loadingExceptions
    ? ["Loading..."]
    : exceptions && Array.isArray(exceptions)
      ? exceptions
      : [];

  const INPUT_WIDTH = "100%";

  const {
    region,
    siteAttribute,
    itemAttribute,
    mrpController,
    materialType,
    exception,
    inApp,
    email,
    timeMode,
    weeksRange,
    dateRange,
  } = currentFilters;

  const availablePlants = useMemo(() => {
    return apiPlants;
  }, [apiPlants]);

  const availableMrpControllersList = useMemo(() => {
    return apiMrpControllers;
  }, [apiMrpControllers]);

  const handleGlobalToggle = (e) => {
    const isChecked = e.target.checked;
    setIsGlobalAlertsEnabled(isChecked);

    setCategoryStatus(
      categories.reduce((acc, category) => {
        acc[category] = isChecked;
        return acc;
      }, {})
    );
  };

  const generateSummary = (
    reg,
    site,
    matType,
    item,
    mrp,
    exc,
    inApp,
    email,
    category
  ) => {
    const channel = [inApp && "App", email && "Email"]
      .filter(Boolean)
      .join(" ");

    let summary = `Alert me on ${channel} for ${category} when:\n`;

    if (reg.length > 0) summary += `- Region: ${reg.join(", ")}\n`;
    if (site.length > 0) summary += `- Plant: ${site.join(", ")}\n`;
    if (matType.length > 0)
      summary += `- Material Type: ${matType.join(", ")}\n`;
    if (item.length > 0) summary += `- Material: ${item.join(", ")}\n`;
    if (mrp.length > 0) summary += `- MRP Controller: ${mrp.join(", ")}\n`;
    if (exc.length > 0) summary += `- Exception: ${exc.join(", ")}\n`;
    if (category === "Inventory") {
      if (timeMode === "weeks")
        summary += `- Time Horizon: ${weeksRange[0]} to ${weeksRange[1]} weeks\n`;
      else if (timeMode === "date")
        summary += `- Date Range: ${dateRange.start} to ${dateRange.end}\n`;
    }

    return summary.trim();
  };

  const formatCategoryForPayload = (category) => {
    const categoryMap = {
      "Inventory": "inventory",
      "Purchase Order": "purchaseOrders",
      "Inbound Shipment": "inboundShipments",
      "Sales Order": "salesOrders",
      "Outbound Shipment": "outboundShipments",
      "Stock Transfer": "stockTransfer",
    };
    return categoryMap[category] || category;
  };

  const currentSummary = useMemo(() => {
    return generateSummary(
      region,
      siteAttribute,
      materialType,
      itemAttribute,
      mrpController,
      exception,
      inApp,
      email,
      selectedCategory
    );
  }, [
    region,
    siteAttribute,
    materialType,
    itemAttribute,
    mrpController,
    exception,
    inApp,
    email,
    selectedCategory,
  ]);

  const handleSave = async () => {
    if (!currentSummary || (!inApp && !email)) return;

    const preferredContact = [];
    if (inApp) preferredContact.push("App");
    if (email) preferredContact.push("Email");

    const existingCondition =
      editingConditionId !== null
        ? allSavedConditions.find((c) => c.id === editingConditionId)
        : null;

    const alertStatusValue = existingCondition
      ? existingCondition.alertStatus !== false
      : true;

    const payload = {
      userEmail: userEmail,
      alertCategory: formatCategoryForPayload(selectedCategory),
      plant: siteAttribute,
      material: itemAttribute,
      mrpController: mrpController,
      preferredContact: preferredContact,
      exceptionTypes: exception,
      regions: region,
      alertName: alertName,
      alertStatus: alertStatusValue,
      materialType: materialType,
      byWeeksStart: weeksRange?.[0] ?? 0,
      byWeeksEnd: weeksRange?.[1] ?? 0,
      byStartDate: dateRange?.start || null,
      byEndDate: dateRange?.end || null,
      category: formatCategoryForPayload(selectedCategory),
    };

    const duplicateName = allSavedConditions.some(
      (cond) =>
        cond.category === selectedCategory &&
        cond.id !== editingConditionId &&
        cond.alertName.trim().toLowerCase() === alertName.trim().toLowerCase()
    );

    if (duplicateName) {
      setErrorPopupMessage("Alert name already exists");
      setErrorPopupOpen(true);
      setIsSaving(false);
      return;
    }

    try {
      setIsSaving(true);

      let savedAlertData = null;

      if (editingConditionId !== null) {
        savedAlertData = await updateAlertSubscription(
          editingConditionId,
          payload
        );
      } else {
        savedAlertData = await saveAlertSubscription(payload);
      }

      if (savedAlertData?.errorMessage) {
        setErrorPopupMessage(savedAlertData.errorMessage);
        setErrorPopupOpen(true);
        setIsSaving(false);
        return;
      }

      const newCondition = {
        ...currentFilters,
        category: selectedCategory,
        summary: currentSummary,
        alertName: alertName,
        alertStatus: alertStatusValue,
        id: savedAlertData?.id || editingConditionId || Date.now(),
      };

      if (editingConditionId !== null) {
        setAllSavedConditions((prev) =>
          prev.map((cond) =>
            cond.id === editingConditionId
              ? { ...newCondition, id: editingConditionId }
              : cond
          )
        );
      } else {
        setAllSavedConditions((prev) => [...prev, newCondition]);
      }

      setCurrentFilters(initialFilterState);
      setEditingConditionId(null);
      setAlertName("");
      setIsSaving(false);
    } catch (err) {
      console.log("API ERROR:", err?.response?.data || err);

      const msg =
        err?.response?.data?.message || err?.message || "Failed to save alert";

      setErrorPopupMessage(msg);
      setErrorPopupOpen(true);
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setCurrentFilters(initialFilterState);
    setAlertName("");
    setEditingCondition(null);
    setEditingConditionId(null);
    setIsEditHydrated(false);
  };



  const handleEditCondition = (conditionToEdit) => () => {
    setAlertName(conditionToEdit.alertName || "");
    setSelectedCategory(conditionToEdit.category);

    setEditingCondition(conditionToEdit);
    setEditingConditionId(conditionToEdit.id);
    setIsEditHydrated(false);

    setCurrentFilters({
      region: conditionToEdit.region || [],
      siteAttribute: conditionToEdit.siteAttribute || [],
      materialType: conditionToEdit.materialType || [],
      mrpController: conditionToEdit.mrpController || [],
      itemAttribute: conditionToEdit.itemAttribute || [],
      exception: conditionToEdit.exception || [],
      inApp: conditionToEdit.inApp || false,
      email: conditionToEdit.email !== false,
      timeMode: conditionToEdit.timeMode || "weeks",
      weeksRange: conditionToEdit.weeksRange || [0, 52],
      dateRange: conditionToEdit.dateRange || { start: null, end: null },
    });
  };



  const handleDeleteCondition = (idToDelete) => async (e) => {
    e.stopPropagation();
    if (!idToDelete) return;
    try {
      await deleteAlertSubscription(idToDelete);
      setAllSavedConditions((prev) =>
        prev.filter((cond) => cond.id !== idToDelete)
      );
      if (editingConditionId === idToDelete) {
        setEditingConditionId(null);
        handleReset();
      }
    } catch (err) {
      console.error("Failed to delete alert:", err);
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        "Failed to delete alert";
      setErrorPopupMessage(msg);
      setErrorPopupOpen(true);
    }
  };

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    handleReset();
    setEditingConditionId(null);
  };

  
  const isInventory = selectedCategory === "Inventory";

 const isSaveEnabled =
  alertName.trim() &&
  region.length > 0 &&
  exception.length > 0 &&
  (inApp || email) &&
  !isSaving;

  const handleMultiSelectChange = (field) => (e) => {
    const newValues = e.target.value;

    setCurrentFilters((prev) => {
      return { ...prev, [field]: newValues };
    });
  };

  const handleIndividualCategoryToggle = (category) => (e) => {
    setCategoryStatus((prev) => ({
      ...prev,
      [category]: e.target.checked,
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        bgcolor: "#F5F5F5",
        p: 2,
        fontFamily: "Inter",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: 240,
          p: 2,
          mr: 2,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2} color="#1A535C">
          Alert Categories
        </Typography>
        {categories.map((cat) => (
          <Box
            key={cat}
            onClick={() => {
              handleCategoryChange(cat);
            }}
            sx={{
              cursor: "pointer",
              px: 1.5,
              py: 1,
              mb: 1,
              borderRadius: 1,
              textAlign: "left",
              backgroundColor:
                selectedCategory === cat ? "#E3F2F4" : "transparent",
              fontWeight: selectedCategory === cat ? 600 : 400,
              color: selectedCategory === cat ? "#1A535C" : "#333",
              borderLeft:
                selectedCategory === cat
                  ? "4px solid #A6C0C4"
                  : "4px solid transparent",
              fontSize: 14,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor:
                  selectedCategory === cat ? "#E3F2F4" : "#F8F8F8",
              },
            }}
          >
            <span>{cat}</span>
          </Box>
        ))}
      </Paper>

      <Box sx={{ flexGrow: 1, maxWidth: 900, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            py: 1,
            pl: 1,
            pr: 3,
            borderRadius: 1,
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={onBack}
              size="small"
              sx={{ mr: 1, color: "#1A535C" }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700} color="#1A535C">
              Manage Alert Settings
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={isGlobalAlertsEnabled}
                onChange={handleGlobalToggle}
                size="small"
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight={600} ml={1}>
                {isGlobalAlertsEnabled ? "On" : "Off"}
              </Typography>
            }
            labelPlacement="start"
            sx={{
              ml: 3,
              color: isGlobalAlertsEnabled ? "#1A535C" : "#666",
            }}
          />
        </Box>

        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: "#fff",
            border: "1px solid #dce3e8",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} color="#1A535C">
              {selectedCategory}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={categoryStatus[selectedCategory]}
                  onChange={handleIndividualCategoryToggle(selectedCategory)}
                  size="small"
                  color="primary"
                  disabled={!isGlobalAlertsEnabled}
                />
              }
              label={categoryStatus[selectedCategory] ? "On" : "Off"}
              labelPlacement="start"
              sx={{
                ml: 1,
                color:
                  categoryStatus[selectedCategory] && isGlobalAlertsEnabled
                    ? "#1A535C"
                    : "#666",
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ opacity: categoryStatus[selectedCategory] ? 1 : 0.5 }}>
            <Accordion
              defaultExpanded
              disabled={
                !categoryStatus[selectedCategory] || !isGlobalAlertsEnabled
              }
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor:
                    editingConditionId !== null ? "#FFFBEA" : "#FAFAFA",
                  borderRadius: 1,
                }}
              >
                <Typography fontWeight={700} fontSize={14} color="#1A535C">
                  {editingConditionId !== null
                    ? "Editing Condition"
                    : "Define Condition"}
                </Typography>
              </AccordionSummary>
              <Box
                sx={{
                  m: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1, color: "#1A535C" }}
                >
                  Alert Name
                </Typography>

                <Autocomplete
                  freeSolo
                  options={alertNameOptions}
                  value={alertName}
                  onChange={(e, newValue) => setAlertName(newValue || "")}
                  onInputChange={(e, newValue) => setAlertName(newValue)}
                  sx={{ width: INPUT_WIDTH }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select or enter a unique alert name"
                      size="small"
                      helperText={
                        loadingSavedConditions
                          ? "Loading alert names..."
                          : "Select from existing alerts or create new"
                      }
                    />
                  )}
                />
              </Box>
              <AccordionDetails sx={{ pt: 2 }}>
                <Typography variant="body2" mb={2}>
                  Configure an alert for{" "}
                  <strong>
                    {selectedCategory == "Purchase Order"
                      ? "Purchase Orders / Stock Transfer"
                      : selectedCategory}
                  </strong>{" "}
                  when the following conditions are met:
                </Typography>

                <Grid
                  container
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(1, 1fr)",
                    gap: 2,
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    mt: 2,
                  }}
                >
                  <Grid item>
                    <MultiSelectCheckbox
                      label="Exceptions"
                      value={exception}
                      options={availableExceptions}
                      onChange={handleMultiSelectChange("exception")}
                      width={INPUT_WIDTH}
                      isLoading={loadingExceptions}
                    />
                  </Grid>
                  <Grid item>
                    <MultiSelectCheckbox
                      label="Region"
                      value={region}
                      options={apiRegions}
                      onChange={handleMultiSelectChange("region")}
                      width={INPUT_WIDTH}
                      isLoading={loadingRegions}
                      enableSearch={false}
                    />
                  </Grid>

                  <Grid item>
                    <MultiSelectCheckbox
                      label="Plant"
                      value={siteAttribute}
                      options={availablePlants}
                      onChange={handleMultiSelectChange("siteAttribute")}
                      disabled={region.length === 0 || loadingPlants}
                      width={INPUT_WIDTH}
                      isLoading={loadingPlants}
                    />
                  </Grid>

                  <Grid item>
                    <MultiSelectCheckbox
                      label="Material Type"
                      value={materialType}
                      options={apiMaterialTypes}
                      onChange={handleMultiSelectChange("materialType")}
                      disabled={
                        siteAttribute.length === 0 || loadingMaterialTypes
                      }
                      width={INPUT_WIDTH}
                      isLoading={loadingMaterialTypes}
                    />
                  </Grid>

                  {selectedCategory === "Inventory" && (
                    <Grid item>
                      <MultiSelectCheckbox
                        label="MRP Controller"
                        value={mrpController}
                        options={availableMrpControllersList}
                        onChange={handleMultiSelectChange("mrpController")}
                        width={INPUT_WIDTH}
                        isLoading={loadingMrpControllers}
                        disabled={
                          siteAttribute.length === 0 || materialType.length === 0
                        }
                      />
                    </Grid>
                  )}

                  <Grid item>
                    <Autocomplete
                      multiple
                      options={apiMaterials}
                      value={itemAttribute}
                      loading={loadingMaterials}
                      disableCloseOnSelect
                      onChange={(e, newValue) =>
                        handleMultiSelectChange("itemAttribute")({
                          target: { value: newValue },
                        })
                      }
                      onInputChange={(e, newInput) => {
                        setMaterialSearchText(newInput);
                        setMaterialPage(0);
                        setHasMoreMaterials(true);
                      }}
                      ListboxProps={{
                        onScroll: (event) => {
                          const listbox = event.currentTarget;
                          if (
                            listbox.scrollTop + listbox.clientHeight >=
                            listbox.scrollHeight - 5 &&
                            hasMoreMaterials &&
                            !loadingMaterials
                          ) {
                            setMaterialPage((prev) => prev + 1);
                          }
                        },
                        style: { maxHeight: 250 },
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={selected} sx={{ mr: 1 }} />
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Material"
                          size="small"
                          placeholder="Type at least 3 characters"
                          helperText={
                            materialSearchText.length > 0 &&
                              materialSearchText.length < 3
                              ? "Enter minimum 3 characters"
                              : ""
                          }
                        />
                      )}
                    />

                  </Grid>

                  

                  {selectedCategory === "Inventory" && (
                    <Grid item>
                      <Box sx={{ width: "100%" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ color: "#1A535C" }}
                          >
                            Time Horizon
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                          <FormControlLabel
                            control={
                              <Radio
                                checked={timeMode === "weeks"}
                                onChange={() =>
                                  setCurrentFilters((prev) => ({
                                    ...prev,
                                    timeMode: "weeks",
                                  }))
                                }
                              />
                            }
                            label="By Weeks"
                          />

                          <FormControlLabel
                            control={<Radio disabled />}
                            label="By Date Range"
                          />
                        </Box>

                        {timeMode === "weeks" && (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Box sx={{ width: "35%" }}>
                              <Slider
                                value={weeksRange}
                                min={0}
                                max={52}
                                valueLabelDisplay="auto"
                                onChange={(e, newVal) =>
                                  setCurrentFilters((prev) => ({
                                    ...prev,
                                    weeksRange: newVal,
                                  }))
                                }
                              />
                            </Box>

                            <Box sx={{ display: "flex", gap: 2 }}>
                              <Button
                                variant="outlined"
                                onClick={handleReset}
                                disabled={isSaving}
                                sx={{
                                  color: "#1A535C",
                                  borderColor: "#A6C0C4",
                                }}
                              >
                                {editingConditionId !== null
                                  ? "Cancel Edit"
                                  : "Reset"}
                              </Button>

                              <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={!isSaveEnabled}
                                sx={{
                                  backgroundColor: "#1A535C",
                                  "&:hover": { backgroundColor: "#14424A" },
                                }}
                              >
                                {isSaving
                                  ? "Saving..."
                                  : editingConditionId !== null
                                    ? "Update Condition"
                                    : "Save Preferences"}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {selectedCategory !== "Inventory" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 3,
                      gap: 2,
                    }}
                  >
                    {saveError && (
                      <Typography
                        color="error"
                        sx={{ mr: 2, alignSelf: "center" }}
                      >
                        {saveError}
                      </Typography>
                    )}

                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      disabled={isSaving}
                      sx={{ color: "#1A535C", borderColor: "#A6C0C4" }}
                    >
                      {editingConditionId !== null ? "Cancel Edit" : "Reset"}
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={!isSaveEnabled}
                      sx={{
                        backgroundColor: "#1A535C",
                        "&:hover": { backgroundColor: "#14424A" },
                      }}
                    >
                      {isSaving
                        ? "Saving..."
                        : editingConditionId !== null
                          ? "Update Condition"
                          : "Save Preferences"}
                    </Button>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
            {loadingSavedConditions ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 3,
                  py: 3,
                }}
              >
                <CircularProgress size={30} sx={{ mr: 2 }} />
                <Typography>Loading saved alert conditions...</Typography>
              </Box>
            ) : savedConditions.length > 0 ? (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  mb={1}
                  color="#1A535C"
                >
                  Saved Conditions ({savedConditions.length})
                </Typography>
                {savedConditions.map((condition) => (
                  <Accordion
                    key={condition.id}
                    defaultExpanded={false}
                    sx={{
                      mb: 1,
                      border: "1px solid #D1D5DB",
                      borderRadius: 2,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "#F1F5F9",
                        borderRadius: 2,
                        "& .MuiAccordionSummary-content": {
                          margin: "12px 0",
                          alignItems: "center",
                          justifyContent: "space-between",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          mr: 2,
                          pr: 2,
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        <Typography variant="caption" color="#666">
                          {condition.category}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexGrow: 1,
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          color="#1A535C"
                        >
                          {condition.alertName || "Unnamed Alert"}
                        </Typography>

                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={condition.alertStatus}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                setAllSavedConditions((prev) =>
                                  prev.map((c) =>
                                    c.id === condition.id
                                      ? {
                                        ...c,
                                        alertStatus: e.target.checked,
                                      }
                                      : c
                                  )
                                );
                              }}
                            />
                          }
                          label={condition.alertStatus ? "On" : "Off"}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          flexShrink: 0,
                          ml: 2,
                        }}
                      >
                        <IconButton
                          onClick={handleEditCondition(condition)}
                          size="small"
                          sx={{
                            color: "#6B7280",
                            "&:hover": { color: "#1A535C" },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={handleDeleteCondition(condition.id)}
                          size="small"
                          sx={{
                            color: "#EF4444",
                            "&:hover": { color: "#B91C1C" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        pt: 2,
                        backgroundColor: "#fff",
                        borderRadius: "0 0 8px 8px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.6,
                          color: "#333",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {condition.summary}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Box sx={{ mt: 3, textAlign: "center", py: 2 }}>
                <Typography color="#999">
                  No saved conditions available
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      <Dialog open={errorPopupOpen} onClose={() => setErrorPopupOpen(false)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography>{errorPopupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorPopupOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertPreference;